// src/screens/mypage/InquiriesScreen.js
// 72차 신규: 문의사항 응대 화면 — 매장관리자(mode:'store')와 총괄 관리자(mode:'app')가
// 공용으로 쓴다. 매장관리자는 자기 매장(들)에 온 문의만, 총괄 관리자는 앱 전체 문의만 본다.
// 75차: 자체 커스텀 헤더 → 공통 AppHeader로 교체(다른 화면과 동일 패턴 누락분)
// 75차: mode:'app'에 "문의사항(사용자)"/"문의사항(매장)" 필터 추가 — 정산 이의제기 등
// 매장관리자가 보내는 문의(senderRole:'store')를 일반 회원 문의와 구분해서 볼 수 있게
// 78차 65차: 단발성 답변 → replies 서브컬렉션 기반 스레드로 교체(대댓글 지원)
import React, { useState, useEffect, useCallback } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView, ActivityIndicator, Image,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { MenuStoreIcon, MenuNicknameIcon, IconLabel, MenuReservationIcon, CheckmarkIcon, HourglassIcon, MenuInquiryIcon } from '../../components/common/Icons';
import { LogoMark } from '../../components/common/AppHeader';
import { getStoreInquiries, getAppInquiries, getInquiryReplies, addInquiryReply } from '../../services/inquiryService';
import { markNotificationsReadByType } from '../../services/recruitService';
import { COLORS } from '../../constants/theme';
import { getInquiryCategoryLabel } from '../../constants';
import { toLocalYMD } from '../../utils/dateUtils';

const GOLD = COLORS.gold, BG = COLORS.bg, SURFACE = COLORS.surface, SURFACE2 = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text, TEXT_MUTED = COLORS.textMuted;
const GREEN = COLORS.green;

const CONTEXT_LABEL = {
  reservation: { label: '예약 문의', Icon: MenuReservationIcon },
  theme:       { label: '테마 문의', Icon: LogoMark },
};

function InquiryCard({ item, adminUid, onReplied }) {
  // 78차 65차: 단발성 답변 대신 replies 서브컬렉션 기반 스레드 — 문의가 한 번에
  // 해결되지 않을 수 있어(문의자 재문의 → 관리자 재답변) 여러 번 주고받을 수 있게 함.
  const [replies, setReplies] = useState([]);
  const [repliesLoaded, setRepliesLoaded] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  // 78차 67차: 카드마다 본문+스레드+답변 입력창이 항상 펼쳐져 있어 목록이 과도하게
  // 길어지던 것 — 기본은 접힌 상태(헤더만), 탭하면 펼침.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    getInquiryReplies(item.id).then(r => { setReplies(r); setRepliesLoaded(true); }).catch(() => setRepliesLoaded(true));
  }, [item.id]);

  // 78차 68차: 78차 65차 스레드 마이그레이션 이전(또는 그 배포 직전 캐시된 클라이언트)의
  // 단일 reply 필드({text, adminUid, repliedAt})만 있고 replies 서브컬렉션은 0건인
  // 문의가 실제로 있었음(라이브 확인) — 그 경우 답변 내용 자체가 화면에서 통째로 사라지고
  // 후속 답장 입력창도 안 뜨던 버그. replies가 비어있으면 이 구버전 필드를 폴백으로 사용.
  const effectiveReplies = replies.length > 0
    ? replies
    : (item.reply?.text ? [{ role: 'admin', text: item.reply.text, createdAt: item.reply.repliedAt, legacy: true }] : []);

  // 78차 67차: 상태 배지가 목록 로드 시점의 item.status(스냅샷)를 그대로만 보여줘서,
  // 문의자가 답변 이후 후속 메시지를 보내(재오픈) 실제로는 "대기중"인데도 배지는 계속
  // "답변완료"로 남아있던 버그 — 매번 새로 조회하는 replies 스레드의 마지막 메시지가
  // 누구 것인지로 상태를 직접 계산(목록 새로고침 없이도 항상 정확함). effectiveReplies가
  // 0건이면(구버전 단일 reply도 없는 진짜 미답변) item.status로 폴백.
  const derivedStatus = repliesLoaded
    ? (effectiveReplies.length === 0 ? item.status : (effectiveReplies[effectiveReplies.length - 1].role === 'admin' ? 'answered' : 'pending'))
    : item.status;

  const handleSend = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await addInquiryReply(item.id, { uid: adminUid, role: 'admin', text });
      setReplies(prev => [...prev, { role: 'admin', uid: adminUid, text: text.trim(), createdAt: new Date() }]);
      onReplied({ ...item, status: 'answered' });
      setText('');
    } catch (e) {
      showAlert('오류', '답변 저장 중 오류가 발생했어요.\n' + e.message);
    } finally { setSaving(false); }
  };

  return (
    <View style={s.card}>
      <TouchableOpacity activeOpacity={0.7} onPress={() => setExpanded(v => !v)}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          {item.targetType === 'app' && (
            <View style={[s.ctxBadge, { flexDirection: 'row', alignItems: 'center', gap: 3 }]}>
              <IconLabel Icon={item.senderRole === 'store' ? MenuStoreIcon : MenuNicknameIcon} size={10} color={GOLD} gap={3} textStyle={s.ctxBadgeTxt}>
                {item.senderRole === 'store' ? '매장' : '사용자'}
              </IconLabel>
            </View>
          )}
          {item.context?.type && (
            <View style={s.ctxBadge}>
              {CONTEXT_LABEL[item.context.type]
                ? <IconLabel Icon={CONTEXT_LABEL[item.context.type].Icon} size={11} color={s.ctxBadgeTxt.color} textStyle={s.ctxBadgeTxt}>{CONTEXT_LABEL[item.context.type].label}</IconLabel>
                : <Text style={s.ctxBadgeTxt}>문의</Text>}
            </View>
          )}
          {/* 78차 35차: 문의 카테고리 배지 — 신규 문의부터 존재(과거 문의는 category 없음) */}
          {!!item.category && (
            <View style={s.ctxBadge}><Text style={s.ctxBadgeTxt}>{getInquiryCategoryLabel(item.targetType, item.category) || item.category}</Text></View>
          )}
          <View style={[s.statusBadge, derivedStatus === 'answered' ? s.statusAnswered : s.statusPending]}>
            <IconLabel Icon={derivedStatus === 'answered' ? CheckmarkIcon : HourglassIcon} size={11}
              color={derivedStatus === 'answered' ? GREEN : GOLD}
              textStyle={[s.statusTxt, { color: derivedStatus === 'answered' ? GREEN : GOLD }]}>
              {derivedStatus === 'answered' ? '답변완료' : '대기중'}
            </IconLabel>
          </View>
          {/* 78차: UTC 기준(toISOString)이던 것을 로컬 기준으로 교체 — 한국 새벽에 작성한
              문의가 하루 전 날짜로 표시되던 버그 */}
          <Text style={s.date}>{toLocalYMD(item.createdAt?.toDate?.() || new Date(item.createdAt || 0))}</Text>
        </View>

        <Text style={s.title} numberOfLines={expanded ? undefined : 1}>{item.title}</Text>
        {!expanded && (
          <Text style={s.expandHint}>{effectiveReplies.length > 0 ? `답장 ${effectiveReplies.length}건 · ` : ''}탭하여 {expanded ? '접기' : '펼치기'}</Text>
        )}
      </TouchableOpacity>

      {expanded && (
        <>
          {item.context?.productName && (
            <Text style={s.ctxSub}>관련: {item.context.productName}</Text>
          )}
          <Text style={s.content}>{item.content}</Text>
          {/* 테마수정/추가/삭제 문의 첨부 이미지 */}
          {!!item.imageUrl && (
            <Image source={{ uri: item.imageUrl }} style={s.attachedImage} />
          )}

          {/* 78차 65차: 답변 스레드 — 문의자 후속 메시지/관리자 재답변까지 전부 시간순 표시 */}
          {!repliesLoaded ? (
            <ActivityIndicator size="small" color={GOLD} style={{ marginTop: 10 }} />
          ) : effectiveReplies.length > 0 ? (
            <View style={{ marginTop: 10, gap: 8 }}>
              {effectiveReplies.map((r, i) => (
                <View key={r.id || i} style={[s.replyBox, r.role === 'user' && s.replyBoxUser]}>
                  {r.role === 'admin'
                    ? <IconLabel Icon={MenuInquiryIcon} size={12} color={GOLD} textStyle={s.replyLabel}>답변</IconLabel>
                    : <Text style={s.replyLabel}>↩️ 문의자 답장</Text>}
                  <Text style={s.replyText}>{r.text}</Text>
                </View>
              ))}
            </View>
          ) : null}

          {repliesLoaded && (
            <View style={{ marginTop: 10 }}>
              <TextInput
                style={s.input}
                value={text}
                onChangeText={setText}
                placeholder={effectiveReplies.length > 0 ? '추가 답변을 입력해주세요' : '답변을 입력해주세요'}
                placeholderTextColor={TEXT_MUTED}
                multiline
              />
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                <GradientButton
                  label={saving ? '저장 중...' : '💾 답변 보내기'}
                  onPress={handleSend}
                  disabled={saving}
                  style={{ flex: 1 }}
                />
              </View>
            </View>
          )}

          <TouchableOpacity onPress={() => setExpanded(false)} style={{ alignSelf: 'center', marginTop: 10 }}>
            <Text style={{ color: TEXT_MUTED, fontSize: 12 }}>▲ 접기</Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  );
}

export default function InquiriesScreen({ navigation, route }) {
  const insets = useSafeAreaInsets();
  const { user, profile } = useAuth();
  const mode = route?.params?.mode || 'store'; // 'store' | 'app'

  const [inquiries, setInquiries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  // 75차: mode:'app'은 일반 회원/매장관리자 문의가 섞여 있어(senderRole) 필터 추가 —
  // 정산 이의제기 등 매장 쪽 문의를 총괄관리자가 놓치지 않고 따로 볼 수 있게
  const [senderFilter, setSenderFilter] = useState('all'); // 'all' | 'user' | 'store'

  const managedStoreIds = Array.isArray(profile?.managedStores)
    ? profile.managedStores
    : (profile?.managedStores ? [profile.managedStores] : []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = mode === 'app' ? await getAppInquiries() : await getStoreInquiries(managedStoreIds);
      setInquiries(list);
    } catch { /* 무음 처리 */ }
    finally { setLoading(false); }
    // 78차 68차: "고객 문의 답변"/"앱 문의" 화면에 들어와 이미 내용을 봤는데도 마이탭
    // 배지가 안 없어지던 버그 — 진입 시 관련 알림을 읽음 처리(모드별 타입 구분).
    // 79차: [버그 수정] 이번 세션에 추가한 inquiry_followup(문의자 후속 메시지 알림)이
    // 이 목록에 빠져 있어서, 관리자가 여기서 후속 메시지를 실제로 읽고 답변해도
    // 그 알림의 뱃지만 영영 안 사라지고 있었음(문의/재예약 화면 재정독 fork로 발견 —
    // 바로 위 두 줄이 이미 겪었던 것과 동일한 유형의 "형제 알림 타입 추가 시 읽음처리
    // 목록에 반영 누락").
    if (user?.uid) {
      const types = mode === 'app' ? ['new_inquiry', 'inquiry_followup'] : ['new_store_inquiry', 'inquiry_followup'];
      markNotificationsReadByType(user.uid, types).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, managedStoreIds.join(','), user?.uid]);

  useEffect(() => { load(); }, [load]);

  const visibleInquiries = mode === 'app'
    ? inquiries.filter(q => senderFilter === 'all' || (q.senderRole || 'user') === senderFilter)
    : inquiries;

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={s.headerTitle}>{mode === 'app' ? '앱 문의사항' : '매장 문의사항'}</Text>} />

      {mode === 'app' && (
        <View style={{ flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingTop: 12 }}>
          {[{k:'all',l:'전체'},{k:'user',l:'문의사항(사용자)'},{k:'store',l:'문의사항(매장)'}].map(f => (
            <TouchableOpacity key={f.k}
              style={[s.filterChip, senderFilter===f.k && s.filterChipOn]}
              onPress={() => setSenderFilter(f.k)}>
              <Text style={[s.filterChipTxt, senderFilter===f.k && s.filterChipTxtOn]}>{f.l}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {loading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color={GOLD} />
        </View>
      ) : (
        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom + 32, gap: 10 }}>
          {visibleInquiries.length === 0 ? (
            <Text style={{ color: TEXT_MUTED, textAlign: 'center', marginTop: 40, fontStyle: 'italic' }}>
              아직 등록된 문의가 없어요.
            </Text>
          ) : (
            visibleInquiries.map(item => (
              <InquiryCard
                key={item.id}
                item={item}
                adminUid={user?.uid}
                onReplied={(updated) => setInquiries(prev => prev.map(q => q.id === updated.id ? updated : q))}
              />
            ))
          )}
        </ScrollView>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  container:   { flex: 1, backgroundColor: BG },
  headerTitle: { color: TEXT, fontSize: 15, fontWeight: '800' },

  card:        { backgroundColor: SURFACE, borderRadius: 14, borderWidth: 1, borderColor: BORDER, padding: 14 },
  ctxBadge:    { backgroundColor: GOLD + '18', borderRadius: 6, borderWidth: 1, borderColor: GOLD + '40', paddingHorizontal: 8, paddingVertical: 2 },
  ctxBadgeTxt: { color: GOLD, fontSize: 10, fontWeight: '700' },
  filterChip:     { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20, borderWidth: 1, borderColor: BORDER, backgroundColor: SURFACE2 },
  filterChipOn:   { backgroundColor: GOLD + '18', borderColor: GOLD + '50' },
  filterChipTxt:  { color: TEXT_MUTED, fontSize: 12 },
  filterChipTxtOn:{ color: GOLD, fontWeight: '700' },
  statusBadge: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2, borderWidth: 1 },
  statusPending:  { backgroundColor: GOLD + '12', borderColor: GOLD + '40' },
  statusAnswered: { backgroundColor: GREEN + '15', borderColor: GREEN + '40' },
  statusTxt:   { fontSize: 10, fontWeight: '700' },
  date:        { color: TEXT_MUTED, fontSize: 11, marginLeft: 'auto' },
  title:       { color: TEXT, fontSize: 14, fontWeight: '800', marginBottom: 4 },
  expandHint:  { color: TEXT_MUTED, fontSize: 11 },
  ctxSub:      { color: TEXT_MUTED, fontSize: 11, marginBottom: 6 },
  content:     { color: TEXT, fontSize: 13, lineHeight: 19 },
  attachedImage: { width: 160, height: 120, borderRadius: 10, marginTop: 8 },

  replyBox:    { backgroundColor: SURFACE2, borderRadius: 10, borderWidth: 1, borderColor: BORDER, padding: 10 },
  replyBoxUser:{ backgroundColor: GOLD + '0d', borderColor: GOLD + '30' }, // 78차 65차: 문의자 답장 구분
  replyLabel:  { color: GOLD, fontSize: 12, fontWeight: '800' },
  replyText:   { color: TEXT, fontSize: 13, marginTop: 6, lineHeight: 18 },

  input:       { backgroundColor: SURFACE2, borderWidth: 1, borderColor: BORDER, borderRadius: 8,
                 paddingHorizontal: 12, paddingVertical: 10, color: TEXT, fontSize: 13, minHeight: 72, textAlignVertical: 'top' },
});
